import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarCheck, Check, CheckCircle2, Copy, Download, Loader2, Mail, MessageSquare, Undo2,
} from "lucide-react";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CopyButton = ({ value, label = "Copy", testid, className = "" }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={async () => {
        const ok = await copyText(value);
        setDone(ok);
        toast[ok ? "success" : "error"](ok ? "Copied" : "Copy it manually");
        if (ok) setTimeout(() => setDone(false), 2000);
      }}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${className || "bg-linen text-navy hover:bg-linen-border"}`}
    >
      {done ? <Check size={12} className="text-teal" /> : <Copy size={12} />} {done ? "Copied" : label}
    </button>
  );
};

const SeasonChip = ({ season }) => {
  if (!season) return null;
  const map = {
    open: { text: `Open · ${season.days_left} days left`, cls: "bg-lime-soft text-navy" },
    before: { text: season.enabled ? `Opens ${season.opens_on}` : "Not scheduled yet", cls: "bg-teal-light text-teal" },
    closed: { text: "Season closed · anniversaries only", cls: "bg-linen text-ink-600" },
  };
  const s = map[season.phase] || map.closed;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${s.cls}`} data-testid="annual-season-chip">
      {s.text}
    </span>
  );
};

export default function AnnualCheckCard({ authHeader, onLogged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [showDrafts, setShowDrafts] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/bdm/annual-check`, { headers: authHeader() });
      setData(d);
    } catch {
      setData(null);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const mark = async (contact, undo = false) => {
    setBusy(contact.id);
    try {
      const { data: d } = await axios.post(
        `${API}/bdm/annual-check/${undo ? "undo" : "check"}`,
        { contact_id: contact.id, channel: "text" },
        { headers: authHeader() },
      );
      setData(d);
      if (!undo) {
        toast.success(`${contact.name.split(" ")[0]} checked — logged to your month-end report`);
        onLogged?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that.");
    } finally {
      setBusy("");
    }
  };

  if (!data) return null;

  const { season, progress, contacts, drafts, assets, share_url: shareUrl } = data;
  const due = contacts.filter((c) => c.due);
  const checked = contacts.filter((c) => c.checked_at);
  const rest = contacts.filter((c) => !c.due && !c.checked_at);

  return (
    <div
      id="annual-check-card"
      data-testid="annual-check-card"
      className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden scroll-mt-24"
    >
      <div className="bg-navy text-white px-6 sm:px-7 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-lime">
            <CalendarCheck size={14} /> {season.label}
          </div>
          <SeasonChip season={season} />
        </div>
        <h3 className="mt-3 font-heading font-medium text-2xl">
          The one campaign you run every year.
        </h3>
        <p className="mt-1.5 text-sm text-white/70 max-w-2xl leading-relaxed">
          Work down your list and ask one question: when did you last check your mortgage? The message
          is written for each person, and every tick lands in your month-end report.
        </p>

        <div className="mt-5">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-semibold" data-testid="annual-progress-label">
              {progress.checked} of {progress.total} checked
            </span>
            <span className="text-white/60 text-xs">
              {progress.remaining} to go{season.open ? ` · closes ${season.closes_on}` : ""}
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-lime transition-all duration-500"
              style={{ width: `${progress.pct}%` }}
              data-testid="annual-progress-bar"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <CopyButton
            value={shareUrl}
            label="Copy my campaign link"
            testid="annual-copy-share-link"
            className="bg-lime text-navy-deep hover:brightness-95"
          />
          <button
            type="button"
            onClick={() => setShowDrafts((v) => !v)}
            data-testid="annual-toggle-drafts"
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <Mail size={12} /> {showDrafts ? "Hide" : "Send to everyone at once"}
          </button>
        </div>
      </div>

      {showDrafts ? (
        <div className="px-6 sm:px-7 py-5 bg-linen/60 border-b border-linen-border space-y-4" data-testid="annual-drafts">
          <p className="text-xs text-ink-500 leading-relaxed">
            Paste this into one email or text and send it to everyone still unchecked
            {drafts.recipients_count ? ` (${drafts.recipients_count}: ${drafts.recipients_hint})` : ""}.
            Your campaign link is already in it, so anything that comes of it is credited to you.
          </p>
          <div className="rounded-2xl bg-white border border-linen-border p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] uppercase tracking-widest text-teal font-bold inline-flex items-center gap-1.5">
                <Mail size={12} /> Email
              </span>
              <div className="flex gap-2">
                <CopyButton value={drafts.email.subject} label="Subject" testid="annual-copy-email-subject" />
                <CopyButton value={drafts.email.body} label="Body" testid="annual-copy-email-body" />
              </div>
            </div>
            <div className="mt-2 text-sm text-navy font-semibold">{drafts.email.subject}</div>
            <pre className="mt-2 whitespace-pre-wrap text-xs text-ink-500 leading-relaxed font-sans">
              {drafts.email.body}
            </pre>
          </div>
          <div className="rounded-2xl bg-white border border-linen-border p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] uppercase tracking-widest text-teal font-bold inline-flex items-center gap-1.5">
                <MessageSquare size={12} /> Text
              </span>
              <CopyButton value={drafts.sms} label="Copy text" testid="annual-copy-sms" />
            </div>
            <p className="mt-2 text-xs text-ink-500 leading-relaxed">{drafts.sms}</p>
          </div>
        </div>
      ) : null}

      <div className="px-6 sm:px-7 py-6">
        {contacts.length === 0 ? (
          <p className="text-sm text-ink-500" data-testid="annual-no-contacts">
            Add a few names to your list and they'll show up here with a message already written.
          </p>
        ) : (
          <div className="space-y-5">
            <Group
              title={season.open ? "Due this season" : "Due for their yearly check"}
              rows={due}
              busy={busy}
              onMark={mark}
              emptyText={
                season.open
                  ? "Everyone on your list has been checked this season. Nice work."
                  : "Nobody is due right now — the season push opens again on " + season.opens_on + "."
              }
            />
            {rest.length > 0 ? (
              <Group title="Waiting for the season" rows={rest} busy={busy} onMark={mark} muted />
            ) : null}
            {checked.length > 0 ? (
              <Group title={`Checked in ${season.year}`} rows={checked} busy={busy} onMark={mark} done />
            ) : null}
          </div>
        )}
      </div>

      <div className="px-6 sm:px-7 py-5 border-t border-linen-border bg-linen/40">
        <p className="text-[11px] uppercase tracking-widest text-ink-400 font-bold">Campaign assets</p>
        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="annual-assets">
          {assets.map((a) => (
            <li key={a.url}>
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="group flex items-start gap-2 rounded-xl border border-linen-border bg-white px-3 py-2.5 hover:border-teal transition-colors"
              >
                <Download size={13} className="text-teal mt-0.5 shrink-0" />
                <span>
                  <span className="block text-xs font-semibold text-navy">{a.label}</span>
                  <span className="block text-[11px] text-ink-500 leading-snug">{a.use}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Group({ title, rows, busy, onMark, emptyText, muted, done }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-ink-400 font-bold">{title}</p>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-ink-500">{emptyText}</p>
      ) : (
        <ul className="mt-3 divide-y divide-linen-border border-y border-linen-border">
          {rows.map((c) => (
            <li key={c.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`annual-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {done ? <CheckCircle2 size={14} className="text-teal shrink-0" /> : null}
                  <span className={`font-semibold ${muted ? "text-ink-500" : "text-navy"}`}>{c.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-ink-400">{c.kind_label}</span>
                </div>
                {done ? (
                  <div className="text-[11px] text-ink-400">Checked {String(c.checked_at).slice(0, 10)}</div>
                ) : c.due_reason === "anniversary" ? (
                  <div className="text-[11px] text-amber-700">A year since their last check</div>
                ) : null}
              </div>
              {done ? (
                <button
                  type="button"
                  onClick={() => onMark(c, true)}
                  data-testid={`annual-undo-${c.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-ink-400 hover:text-navy"
                >
                  <Undo2 size={12} /> Undo
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <CopyButton value={c.script} label="Copy the message" testid={`annual-copy-script-${c.id}`} />
                  <button
                    type="button"
                    disabled={busy === c.id}
                    onClick={() => onMark(c)}
                    data-testid={`annual-mark-${c.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
                  >
                    {busy === c.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Mark checked
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
